<template>
  <ErrorBoundary>
    <div>
      <PublicLayout v-if="(!globalConfigStore.isAuth || route.meta.isPublic === true) && i18nLoaded" />
      <Skeleton v-else-if="globalConfigStore.isLoading"></Skeleton>
      <Layout v-else></Layout>
      <Toasts></Toasts>
      <div v-if="uiSettingsStore.refreshElements.length">
        <div class="fixed t1 l1 bg-orange p-2 rounded">
          <div
            class="spinner-border"
            style="color: white"
            role="status">
            <span class="sr-only"></span>
          </div>
        </div>
      </div>
      <div id="pointedModalSpace" />
      <ConsentModal v-if="globalConfigStore.isAuth && !isReactNative" />
    </div>
  </ErrorBoundary>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import Layout from "./components/layout/Layout.vue";
import Skeleton from "./components/layout/Skeletons/Skeleton.vue";
import Toasts from "./components/layout/Toasts.vue";

import { useRoute, useRouter } from "vue-router";
import PublicLayout from "./components/layout/PublicLayout.vue";
import ConsentModal from "./components/library/ConsentModal/ConsentModal.vue";
import ErrorBoundary from "./components/library/ErrorBoundary/ErrorBoundary.vue";
import { DXVariable } from "./types";
import { parseUserRights } from "./utils/localStorage";
import { useTranslation } from "i18next-vue";
import { mimicLogoutWithoutRedirect } from "./utils/logout";
import { applyColorScheme } from "./themes.js";
import { useAppWrapper } from "./composables/useAppWrapper.js";
import { useReportStore } from "./storeModules/reports/reportStore";
import { useGuidedTourStore } from "./storeModules/guidedtours/GuidedTourStore";
import { useGlobalConfigsStore } from "./storeModules/globalConfigs/globalConfigs";
import { useDeviceStore } from "./storeModules/devices/DeviceStore";
import { useUISettingsStore } from "./storeModules/uisettings/UISettingsStore";
import { useIMStore } from "./storeModules/inventoryManagement/IMStore";
import { useUserConfigStore } from "./storeModules/userConfigs";
import { useMultiTenancyStore } from "./storeModules/multiTenancy/multiTenancyStore";
import { CONSENT_LIST, SERVICE_ID } from "./constants/consentRequiredServicesList";

const { t, i18next } = useTranslation();
const route = useRoute();
const globalConfigStore = useGlobalConfigsStore();
const deviceStore = useDeviceStore();
const reportStore = useReportStore();
const uiSettingsStore = useUISettingsStore();
const imStore = useIMStore();
const userConfigStore = useUserConfigStore();
const guidedTourStore = useGuidedTourStore();
const multiTenancyStore = useMultiTenancyStore();
const router = useRouter();
const { isReactNative, initAppBridge } = useAppWrapper();
const loadingDashboardData = ref(false);
const devices = ref(false);
const variables = ref(false);
const inventory = ref(false);
const news = ref(false);
const i18nLoaded = ref(false);

const RELOAD_INTERVAL_SECONDS = {
  devicesValues: 20,
  devices24h: 60,
  alarms: 20,
  issues: 20,
  inventory: 20,
  news: 120,
  devices: 20,
};
const dataFetchingIntervalIDs = [] as NodeJS.Timeout[];

const loadDashboardData = async () => {
  loadingDashboardData.value = true;
  try {
    uiSettingsStore.fetchDashboardData();
  } catch {
    uiSettingsStore.setError({
      message: t("error.connection_fail_handle_uisettings"),
      type: "error",
    });
  }
};

const loadDevices = async () => {
  try {
    if (globalConfigStore.connector) {
      const devicesData = await globalConfigStore.connector.devices.getDevices();
      if (Array.isArray(devicesData)) {
        deviceStore.devices = devicesData;
        devices.value = true; // set devices for async check
      } else {
        deviceStore.devices = [];
      }
    }
  } catch (error) {
    checkSessionInTheResponse(error as string);
    uiSettingsStore.setError({
      message: t("error.connection_fail_load_devices_text"),
      type: "error",
    });
  }
};

const loadVariables = async () => {
  try {
    if (globalConfigStore.connector) {
      const variablesData = await globalConfigStore.connector.devices.getVariables();
      if (Array.isArray(variablesData)) {
        deviceStore.variables = variablesData as DXVariable[];
        variables.value = true; // set variables for async check
      }
    }
  } catch (error) {
    checkSessionInTheResponse(error as string);
    uiSettingsStore.setError({
      message: t("error.connection_fail_load_current_values_text"),
      type: "error",
    });
  }
};

const loadAlarms = async () => {
  try {
    if (globalConfigStore.connector) {
      const alarms = await globalConfigStore.connector.alarms.getAlarms();
      if (alarms) {
        deviceStore.alarms = alarms ?? [];
      }
    }
  } catch (error) {
    checkSessionInTheResponse(error as string);
    uiSettingsStore.setError({
      message: t("error.connection_fail_load_alarms"),
      type: "error",
    });
  }
};

const loadIssues = async () => {
  try {
    if (globalConfigStore.connector) {
      const issues = await globalConfigStore.connector.devices.getAllIssues();
      if (typeof issues === "string") {
        throw new Error("Get Issues Call Failed");
      }
      const items = deviceStore.setIssues(issues);
      if (!items) {
        return;
      }
      const devicesWithNewIssues = Object.keys(items);
      // We do not create deviceInstances by default. So we need to check if there are deviceInstances in the store that have new issues.
      if (devicesWithNewIssues.length > 0) {
        const deviceInstancesToBeUpdated = Object.keys(deviceStore.deviceInstances).filter((device) => devicesWithNewIssues.includes(device));
        deviceInstancesToBeUpdated.forEach((device) => deviceStore.deviceInstances[device].getDeviceIssues());
      }
    }
  } catch (error) {
    checkSessionInTheResponse(error as string);
    uiSettingsStore.setError({
      message: t("error.connection_fail_load_issues"),
      type: "error",
    });
  }
};

const loadInventory = async () => {
  try {
    if (globalConfigStore.connector) {
      const inventoryData = await globalConfigStore.connector.inventoryManagement.getInventory();
      if (typeof inventoryData == "string") throw new Error("Invalid IM response");
      imStore.setInventory(inventoryData);
      inventory.value = true;
    }
  } catch (error) {
    checkSessionInTheResponse(error as string);
    uiSettingsStore.setError({
      message: t("error.connection_fail_load_inventory"),
      type: "error",
    });
  }
};

const loadNews = async () => {
  try {
    if (globalConfigStore.connector) {
      const newsData = await globalConfigStore.connector.general.getWhatsNewContent();
      if (!Array.isArray(newsData)) {
        userConfigStore.newsList = [];
        return;
      }
      userConfigStore.newsList = newsData;
    }
  } catch (error) {
    checkSessionInTheResponse(error as string);
    uiSettingsStore.setError({
      message: t("error.connection_fail_get_whats-new"),
      type: "error",
    });
  } finally {
    news.value = true;
  }
};

const loadDeviceTypes = async () => {
  try {
    if (globalConfigStore.connector) {
      const types = await globalConfigStore.connector.devices.getDeviceTypes();
      if (typeof types === "string") throw new Error("Get Device Call Failed");
      deviceStore.devicesList = types;
    }
  } catch (error) {
    console.error(error);
  }
};

const loadReportRequests = async () => {
  try {
    if (globalConfigStore.connector) {
      const templates = await globalConfigStore.connector.devices.getReportRequests();
      reportStore.reportRequests = templates;
    }
  } catch (error) {
    console.error("Error fetching values templates:", error);
  }
};
const loadContractData = async () => {
  try {
    await multiTenancyStore.loadMainContract();
  } catch (error) {
    console.error("Error fetching contract data:", error);
  }
};

/**
 * Periodically reloads data from backend to stay up to date
 */
function startRepeatingAPICalls() {
  // TODO (JB): Find better way to stop running this, when user is resetting OTP
  if (!globalConfigStore.isAuth || location.pathname.includes("/reset-otp/")) {
    return;
  }
  if (dataFetchingIntervalIDs.length > 0) {
    return;
  }

  dataFetchingIntervalIDs.push(
    setInterval(loadAlarms, RELOAD_INTERVAL_SECONDS.alarms * 1000),
    setInterval(loadIssues, RELOAD_INTERVAL_SECONDS.issues * 1000),
    setInterval(loadInventory, RELOAD_INTERVAL_SECONDS.inventory * 1000),
    setInterval(loadNews, RELOAD_INTERVAL_SECONDS.news * 1000),
    setInterval(loadDevices, RELOAD_INTERVAL_SECONDS.devices * 1000)
  );
}

function stopRepeatingAPICalls() {
  dataFetchingIntervalIDs.forEach((i) => clearInterval(i));
}

const getAPIData = async () => {
  // TODO (JB): Find better way to stop running this, when user is resetting OTP
  if (!globalConfigStore.isAuth || location.pathname.includes("/reset-otp/")) {
    return;
  }

  if (!globalConfigStore.connector) {
    console.error("No connector");
    return;
  }

  // we need devices and ui-settings first for the other endpoints
  await Promise.all([
    loadDashboardData(), // get-dashboards
    loadDevices(), // get-devices
  ]);
  const parallelLoadingList = [
    loadDeviceTypes(),
    loadVariables(), // get-variables
    loadAlarms(), // get-alarms file-wide
    loadIssues(), // get-issues file-wide
    loadInventory(), // get-inventory-management Inventory Management
    loadNews(), // get changelog etc.
    loadReportRequests(),
    guidedTourStore.initializeTours(),
  ];
  if (userConfigStore.hasAccessToMultiTenancy) {
    parallelLoadingList.push(loadContractData());
  }
  await Promise.all(parallelLoadingList);
};

const checkSessionInTheResponse = (error: string) => {
  if (error !== "Session invalid") return;

  mimicLogoutWithoutRedirect();
  globalThis.location.reload();
  router.push({
    name: "Logout",
  });
};

i18next.on("loaded", () => {
  i18nLoaded.value = true;
});

// reload all data on language change. without showing a loading indicator or similar.
i18next.on("languageChanged", async () => {
  stopRepeatingAPICalls();
  await getAPIData();
  startRepeatingAPICalls();
});

watch(
  () => globalConfigStore.isAuth,
  async (newV) => {
    // login
    if (newV) {
      globalConfigStore.isLoading = true;
      await getAPIData();
      startRepeatingAPICalls();
      globalConfigStore.isLoading = false;
    }
    // logout
    else if (newV === false) {
      stopRepeatingAPICalls();
    }
  }
);

watch(
  () => uiSettingsStore.haveFullRefresh,
  async (newV) => {
    if (newV) {
      globalConfigStore.isLoading = true;
      stopRepeatingAPICalls();
      await getAPIData();
      startRepeatingAPICalls();
      globalConfigStore.isLoading = false;
      uiSettingsStore.haveFullRefresh = false;
    }
  }
);

watch(
  uiSettingsStore.refreshElements,
  async (refreshElements) => {
    // only run, if we have elements in our refresh array.
    if (!refreshElements?.length) return;

    // remove the first element from the array to refresh and use it to indicate what needs refreshing.
    const areaToRefresh = refreshElements.shift();
    // assign the reduced array back to the store, to trigger another check.
    uiSettingsStore.refreshElements = refreshElements;

    // add other areas if needed.
    if (areaToRefresh === "alarms") return loadAlarms();
    if (areaToRefresh === "devices") return loadDevices();
    if (areaToRefresh === "inventory") return loadInventory();
    // currently not used, was called on every refresh before.
    if (areaToRefresh === "variables") return loadVariables();
  },
  { deep: true, immediate: true }
);

function initializeConsentValues() {
  const rememberChoice = localStorage.getItem("rememberConsentChoice");

  if (rememberChoice === "true") {
    const selection = CONSENT_LIST.reduce<
      Partial<{
        [key in SERVICE_ID]: boolean;
      }>
    >((result, service) => {
      const previousSelection = localStorage.getItem(service.id);
      result[service.id] = previousSelection === "true";
      return result;
    }, {});

    uiSettingsStore.setUserConsent(selection);
  }
}

onMounted(() => {
  applyColorScheme(globalConfigStore._theme);
  userConfigStore.userRights = parseUserRights();
  initializeConsentValues();

  initAppBridge();
});
</script>